import useGetState from '@intelligence/hooks/useGetState';

import type { IRecord } from '@gp/types/global';

interface IUseMappingOperations<T> {
  get: (key: string) => T;
  set: (key: string, value: T) => void;
  getAll: () => IRecord<T>;
  setAll: (payload: IRecord<T>) => void;
  remove: (key: string) => void;
  reset: () => void;
  clear: () => void;
}

export default function useMapping<T = any>(initialMapping?: IRecord<T>): [IRecord<T>, IUseMappingOperations<T>] {
  const [mapping, setMapping, getMapping] = useGetState(() => ({ ...initialMapping }));

  const set = (key: string, value: T) => {
    if (!key) return;
    const latestMapping = getMapping();
    const newMapping = { ...latestMapping, [key]: value };
    setMapping(newMapping);
  };

  const get = (key: string): T => {
    const latestMapping = getMapping();
    return latestMapping?.[key];
  };

  const getAll = () => getMapping();

  const setAll = (payload: IRecord<T>) => {
    setMapping(payload || {});
  };

  const remove = (key: string) => {
    const latestMapping = getMapping();
    const newMapping = Object.entries(latestMapping)
      .filter(([k]) => k !== key)
      .reduce((acc, cur) => {
        const [key, value] = cur || [];
        return { ...acc, [key]: value };
      }, {});
    setMapping(newMapping);
  };

  const reset = () => {
    setMapping({ ...initialMapping });
  };

  const clear = () => {
    setMapping({});
  };

  return [mapping, { get, set, getAll, setAll, remove, reset, clear }];
}
